///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2021 - 2025
//                                  RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Tippy from "@tippyjs/react";
import { isEqual } from "lodash";

import IconButton from "../../form-controls/IconButton";
import GridIcon from "../../icons/GridIcon";
import MultiSelect from "../../form-controls/MultiSelect";
import getPricingGridColumns, {
  filterPwbHiddenColumns
} from "../../../helpers/getPricingGridColumns";
import { setTableColumns as setPricingGridColumns } from "../../../slices/bidPricingSlice";
import messages from "../../../helpers/messages";
import { controls } from "../../../helpers/types";

const mapColumnOptions = (columnOrder, role, division, isCoyoteBid, hidePwbCost) => {
  const gridColumns = filterPwbHiddenColumns(
    getPricingGridColumns(role, division, null, isCoyoteBid),
    hidePwbCost
  );

  return columnOrder
    .filter(field => {
      if (!hidePwbCost) {
        return true;
      }

      return field !== "pwbCost" && field !== "pricingModel";
    })
    .map(field => {
    const column = gridColumns.find(c => c.field === field);

    return {
      label: column.columnChooserLabel || column.header,
      value: column.field,
      isFixed: column.requiredColumn
    };
  });
};

export const ColumnChooser = ({ role, division }) => {
  const dispatch = useDispatch();
  const { bidPricing, bidSummary, featureFlags } = useSelector(state => state);
  const hidePwbCost = featureFlags?.hidePwbCost;

  const columnOrder = bidPricing?.columns?.order;
  const isCoyoteBid = bidSummary?.summary?.isCoyoteBid;

  const [show, setShow] = useState(false);
  const [tableColumns, setTableColumns] = useState(
    mapColumnOptions(columnOrder, role, division, isCoyoteBid, hidePwbCost)
  );
  useEffect(() => {
    setTableColumns(mapColumnOptions(columnOrder, role, division, isCoyoteBid, hidePwbCost));
  }, [columnOrder, role, division, isCoyoteBid, hidePwbCost]);

  const toggleChooser = () => {
    setShow(!show);
  };
  const handleChange = value => {
    setTableColumns(value);
  };

  return (
    <Tippy
      placement="bottom-end"
      visible={show}
      onClickOutside={toggleChooser}
      trigger={undefined}
      content={
        <div style={{ width: 360 }} className="card p-16">
          <MultiSelect
            value={tableColumns}
            options={mapColumnOptions(
              filterPwbHiddenColumns(
                getPricingGridColumns(role, division, null, isCoyoteBid),
                hidePwbCost
              )
                .filter(c => !c.onlyLaneMatch && c.control !== controls.hide)
                .map(c => c.field),
              role,
              division,
              isCoyoteBid,
              hidePwbCost
            )}
            onChange={handleChange}
            menuIsOpen={true}
            isSearchable={true}
            noOptionsMessage={({ inputValue }) =>
              inputValue
                ? messages.noOptions.defaultMessage
                : messages.allColumnsSelected.defaultMessage
            }
          />
          <button
            onClick={() => {
              dispatch(
                setPricingGridColumns({
                  columns: tableColumns.map(o => o.value),
                  role,
                  division
                })
              );
              setShow(false);
            }}
            className="primary-button univers-67-bold-cond font-size-14 uppercase mt-12 mx-8 h-32 px-16"
            disabled={
              !tableColumns?.length ||
              isEqual(
                tableColumns.map(c => c.value),
                columnOrder
              )
            }
          >
            {messages.apply.defaultMessage}
          </button>
        </div>
      }
      appendTo={document.body}
      interactive={true}
    >
      <span className="fr">
        <IconButton
          dataTestId="column-chooser-icon"
          icon={GridIcon}
          onClick={toggleChooser}
          title={messages.columnChooser.defaultMessage}
          iconProps={{ style: { transform: `rotate(90deg)` } }}
        ></IconButton>
      </span>
    </Tippy>
  );
};

export default ColumnChooser;
